import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { fetchFundTraining, saveFundTraining } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

// Closed narrative vocabulary — MUST stay in sync with waive-core `NARRATIVE_CONTEXTS`
// (waive_core/config/fund_record.py) and the plugin's NARRATIVE_CONTEXT_MAP. Grouped + labelled
// for authoring; the server re-validates against the contract, so this is a UX aid, not the gate.
const NARRATIVE_GROUPS = [
    {
        heading: 'Fund characterisation',
        contexts: [
            ['portfolio_role', 'Portfolio role', 'The role this fund plays in a portfolio.'],
            ['diversification_role', 'Diversification role', 'How it contributes to diversification.'],
        ],
    },
    {
        heading: 'Suitability by risk band',
        contexts: [
            ['cautious_use', 'Cautious use', 'Use within a cautious portfolio.'],
            ['balanced_use', 'Balanced use', 'Use within a balanced portfolio.'],
            ['growth_use', 'Growth use', 'Use within a growth portfolio.'],
            ['adventurous_use', 'Adventurous use', 'Use within an adventurous portfolio.'],
            ['suitability_narrative', 'General suitability', 'Overall suitability narrative.'],
        ],
    },
    {
        heading: 'Risk',
        contexts: [
            ['exclusion_rationale', 'Exclusion rationale', 'Why/when this fund is excluded.'],
            ['capacity_constraint', 'Capacity constraint', 'Capacity-for-loss considerations.'],
        ],
    },
    {
        heading: 'Cost & conduct',
        contexts: [
            ['cost_justification', 'Cost justification', 'Value-for-money justification.'],
            ['consumer_duty_rationale', 'Consumer Duty rationale', 'Fair-treatment rationale (PRIN 2A).'],
        ],
    },
];
const ALL_CONTEXTS = NARRATIVE_GROUPS.flatMap(g => g.contexts.map(c => c[0]));
const ASSET_CLASSES = ['Equity', 'Bond', 'Cash', 'Mixed'];

// Build the empty authoring form, prefilling the structured classification from the catalogue fund
// (the author confirms/overrides). `provider` is the human provider code/name, not the FK.
function emptyForm(fund, providerLabel) {
    return {
        asset_class: fund?.asset_class || 'Equity',
        region: fund?.geo || '',
        srri: fund?.srri ?? 4,
        ter: fund?.ter != null ? String(fund.ter) : '',
        min_srri_capacity: fund?.srri ?? 4,
        max_cautious_allocation_pct: '',
        client_types: 'retail',
        applicable_regs: '',
        provider: providerLabel || '',
        narrative: Object.fromEntries(ALL_CONTEXTS.map(c => [c, ''])),
    };
}

// Map a stored FundRecord back into the flat form shape.
function formFromRecord(rec, fund, providerLabel) {
    const base = emptyForm(fund, providerLabel);
    if (!rec) return base;
    return {
        asset_class: rec.classification?.asset_class || base.asset_class,
        region: rec.classification?.region || base.region,
        srri: rec.classification?.srri ?? base.srri,
        ter: rec.classification?.ter != null ? String(rec.classification.ter) : base.ter,
        min_srri_capacity: rec.suitability?.min_srri_capacity ?? base.min_srri_capacity,
        max_cautious_allocation_pct: rec.suitability?.max_cautious_allocation_pct != null
            ? String(rec.suitability.max_cautious_allocation_pct) : '',
        client_types: (rec.suitability?.client_types || ['retail']).join(', '),
        applicable_regs: (rec.applicable_regs || []).join(', '),
        provider: rec.provider || base.provider,
        narrative: { ...base.narrative, ...(rec.narrative || {}) },
    };
}

function csv(s) {
    return s.split(',').map(x => x.trim()).filter(Boolean);
}

export default function FundTrainingModal({ show, onHide, fund, providerLabel, onSaved }) {
    const { can } = useAuth();
    const [form, setForm] = useState(emptyForm(null, ''));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        if (!show || !fund) return;
        setError(null);
        setInfo(null);
        setLoading(true);
        fetchFundTraining(fund.id)
            .then(data => setForm(formFromRecord(data.record, fund, providerLabel)))
            .catch(err => {
                if (err.response?.status === 404) {
                    setForm(emptyForm(fund, providerLabel)); // not yet authored — start blank
                } else if (err.response?.status === 503) {
                    setInfo('Fund-training storage is not active yet (waive-core rollout pending). You can draft, but saving will be enabled once the contract ships.');
                    setForm(emptyForm(fund, providerLabel));
                } else {
                    setError('Failed to load existing training data.');
                }
            })
            .finally(() => setLoading(false));
    }, [show, fund, providerLabel]);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
    const setNarrative = (ctx, value) => setForm(f => ({ ...f, narrative: { ...f.narrative, [ctx]: value } }));

    const authoredCount = ALL_CONTEXTS.filter(c => (form.narrative[c] || '').trim()).length;

    const handleSave = async () => {
        setError(null);
        if (!form.region.trim()) { setError('Region is required.'); return; }
        if (form.ter === '') { setError('TER is required.'); return; }
        if (form.max_cautious_allocation_pct === '') { setError('Max cautious allocation % is required.'); return; }

        // Assemble the FundRecord; only include narrative contexts that were actually authored.
        const narrative = {};
        ALL_CONTEXTS.forEach(c => { if ((form.narrative[c] || '').trim()) narrative[c] = form.narrative[c].trim(); });
        const record = {
            fund_id: fund.id,
            name: fund.name,
            isin: fund.isin || '',
            provider: form.provider.trim(),
            classification: {
                asset_class: form.asset_class,
                region: form.region.trim(),
                srri: parseInt(form.srri, 10),
                ter: parseFloat(form.ter),
            },
            suitability: {
                min_srri_capacity: parseInt(form.min_srri_capacity, 10),
                max_cautious_allocation_pct: parseFloat(form.max_cautious_allocation_pct),
                client_types: csv(form.client_types),
            },
            applicable_regs: csv(form.applicable_regs),
            narrative,
        };

        setSaving(true);
        try {
            await saveFundTraining(fund.id, record);
            onSaved?.();
            onHide();
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            if (status === 503) {
                setError('Saving is not active yet — the waive-core FundRecord contract has not shipped to admin-api (ADR-0036 rollout).');
            } else if (Array.isArray(detail)) {
                setError(detail.map(d => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; '));
            } else {
                setError(detail || 'Save failed.');
            }
        } finally {
            setSaving(false);
        }
    };

    if (!fund) return null;

    return (
        <Modal show={show} onHide={onHide} centered size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>
                    Training data — <span style={{ fontFamily: 'monospace' }}>{fund.id}</span>
                    <Badge bg={authoredCount === ALL_CONTEXTS.length ? 'success' : 'secondary'} className="ms-2" style={{ fontSize: 10 }}>
                        {authoredCount}/{ALL_CONTEXTS.length} narratives
                    </Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {info && <Alert variant="info" className="py-2" style={{ fontSize: 13 }}>{info}</Alert>}
                {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : (
                    <Form>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '0 0 8px' }}>CLASSIFICATION</div>
                        <div className="row g-3">
                            <Form.Group className="col-md-3">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Asset class</Form.Label>
                                <Form.Select size="sm" value={form.asset_class} onChange={e => set('asset_class', e.target.value)}>
                                    {ASSET_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="col-md-3">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Region</Form.Label>
                                <Form.Control size="sm" value={form.region} onChange={e => set('region', e.target.value)} placeholder="e.g. Global" />
                            </Form.Group>
                            <Form.Group className="col-md-3">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>SRRI (1–7)</Form.Label>
                                <Form.Control type="number" size="sm" min={1} max={7} value={form.srri}
                                    onChange={e => set('srri', parseInt(e.target.value, 10) || 1)} />
                            </Form.Group>
                            <Form.Group className="col-md-3">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>TER %</Form.Label>
                                <Form.Control type="number" size="sm" min={0} step="0.01" value={form.ter}
                                    onChange={e => set('ter', e.target.value)} placeholder="e.g. 0.75" />
                            </Form.Group>
                        </div>

                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '18px 0 8px' }}>SUITABILITY</div>
                        <div className="row g-3">
                            <Form.Group className="col-md-3">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Min SRRI capacity</Form.Label>
                                <Form.Control type="number" size="sm" min={1} max={7} value={form.min_srri_capacity}
                                    onChange={e => set('min_srri_capacity', parseInt(e.target.value, 10) || 1)} />
                            </Form.Group>
                            <Form.Group className="col-md-3">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Max cautious alloc %</Form.Label>
                                <Form.Control type="number" size="sm" min={0} step="0.1" value={form.max_cautious_allocation_pct}
                                    onChange={e => set('max_cautious_allocation_pct', e.target.value)} placeholder="e.g. 15" />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Client types <span className="text-muted fw-normal">(comma-separated)</span></Form.Label>
                                <Form.Control size="sm" value={form.client_types} onChange={e => set('client_types', e.target.value)} placeholder="retail, professional" />
                            </Form.Group>
                        </div>

                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '18px 0 8px' }}>PROVENANCE</div>
                        <div className="row g-3">
                            <Form.Group className="col-md-6">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Provider</Form.Label>
                                <Form.Control size="sm" value={form.provider} onChange={e => set('provider', e.target.value)} placeholder="e.g. L&G" />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Applicable regs <span className="text-muted fw-normal">(comma-separated)</span></Form.Label>
                                <Form.Control size="sm" value={form.applicable_regs} onChange={e => set('applicable_regs', e.target.value)} placeholder="COBS_9.2.1R, PRIN_2A" />
                            </Form.Group>
                        </div>

                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '18px 0 4px' }}>
                            VERBATIM NARRATIVE
                            <span className="text-muted fw-normal ms-2" style={{ textTransform: 'none' }}>
                                Authored verbatim by compliance — retrieved unchanged at explanation time. Leave blank to omit a context.
                            </span>
                        </div>
                        {NARRATIVE_GROUPS.map(group => (
                            <div key={group.heading} className="mb-2">
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', margin: '10px 0 4px' }}>{group.heading}</div>
                                {group.contexts.map(([ctx, label, hint]) => (
                                    <Form.Group key={ctx} className="mb-2">
                                        <Form.Label style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                                            {label} <span className="text-muted fw-normal" style={{ fontFamily: 'monospace', fontSize: 11 }}>{ctx}</span>
                                        </Form.Label>
                                        <Form.Control as="textarea" rows={2} size="sm" value={form.narrative[ctx]}
                                            onChange={e => setNarrative(ctx, e.target.value)} placeholder={hint} />
                                    </Form.Group>
                                ))}
                            </div>
                        ))}
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || loading || !can.editCatalogue()}
                        title={can.editCatalogue() ? undefined : "Requires the waive-config role"}>
                    {saving && <Spinner size="sm" animation="border" className="me-1" />}
                    Save Training Data
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
